import React, { useState, useEffect, useCallback } from 'react';
import { landlordApi } from '../../services/api';
import texasApi, { CompliancePortfolioSummary, SecurityScoreResult, HvacReadinessSummary } from '../../services/texasApi';
import { TexasComplianceWidgets } from '../TexasCompliance/TexasComplianceWidgets';
import { DepositTrackerWidget } from '../TexasCompliance/DepositTrackerWidget';
import { RekeyStatusWidget } from '../TexasCompliance/RekeyStatusWidget';
import { RentalRegistrationWidget } from '../TexasCompliance/RentalRegistrationWidget';
import { HRRBBanner } from './HRRBBanner';
import { CompactWidget } from '../HousingPro/DashboardWidgets';
import Navbar from '../Dashboard/components/Navbar';
import '../../components/HousingPro/HousingPro.css';

interface TexasDashboardProps {
  user: any;
}

const TexasDashboard: React.FC<TexasDashboardProps> = ({ user }) => {
  const [portfolio, setPortfolio] = useState<CompliancePortfolioSummary | null>(null);
  const [securityScore, setSecurityScore] = useState<SecurityScoreResult | null>(null);
  const [hvac, setHvac] = useState<HvacReadinessSummary | null>(null);
  const [portfolioSize, setPortfolioSize] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadSummary = useCallback(async () => {
    setError(null);
    try {
      // Load stored properties first (tenant-scoped). Texas compliance is computed from
      // stored properties; the GET portfolio endpoint does not read from DB in this app state.
      let properties: any[] = [];
      try {
        const propsRes: any = await landlordApi.get('api/properties');
        if (Array.isArray(propsRes?.data)) properties = propsRes.data;
        else if (Array.isArray(propsRes?.data?.data)) properties = propsRes.data.data;
        else if (Array.isArray(propsRes?.data?.properties)) properties = propsRes.data.properties;
      } catch {
        properties = [];
      }
      setPortfolioSize(properties?.length ?? null);

      const portfolioPromise =
        properties && properties.length > 0
          ? texasApi.complianceDashboard.calculatePortfolio(properties)
          : texasApi.complianceDashboard.getPortfolio();

      const [pf, score, hvacRes] = await Promise.allSettled([
        portfolioPromise,
        texasApi.securityScore.getProperty('portfolio'),
        texasApi.hvacReadiness.getPortfolioSummary(),
      ]);
      if (pf.status === 'fulfilled') setPortfolio(pf.value);
      if (score.status === 'fulfilled') setSecurityScore(score.value);
      if (hvacRes.status === 'fulfilled') setHvac(hvacRes.value);
    } catch (err: any) {
      setError(err?.message || 'Failed to load Texas compliance summary');
    }
  }, []);

  useEffect(() => {
    loadSummary();
    const t = setInterval(loadSummary, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [loadSummary]);

  const scorePct = portfolio?.score ?? (
    securityScore && securityScore.overallMax
      ? Math.round((securityScore.overallScore / securityScore.overallMax) * 100)
      : null
  );

  return (
    <div data-testid="texas-dashboard" className="hp-dashboard-container">
      <Navbar
        expiringSoon={portfolio?.expiringCount ?? 0}
        overdue={portfolio?.expiredCount ?? 0}
      />

      {/* F-145: Houston HRRB alerts (renders only when action is needed) */}
      <HRRBBanner />

      <header className="hp-header">
        <div>
          <div className="flex items-center gap-3">
            <h1>Texas Command Centre</h1>
            <span className="hp-plan-badge" style={{ background: '#dc2626', color: '#fff' }}>TX Market</span>
          </div>
          <p className="hp-welcome">
            {portfolioSize !== null
              ? `Monitoring ${portfolioSize} properties across Texas jurisdictions.`
              : `Compliance overview for ${user?.given_name || 'Admin'}.`}
          </p>
        </div>
        <button
          onClick={loadSummary}
          className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-bold hover:bg-slate-800 transition shadow-sm"
        >
          <i className="fas fa-sync-alt text-xs"></i> Refresh
        </button>
      </header>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-600 mb-4">
          {error}
        </div>
      )}

      <div className="hp-stat-grid">
        <CompactWidget title="Compliance Score" icon="fa-shield-halved" iconColor="slate" defaultExpanded={true} badge={<span className="text-xs text-gray-400">Portfolio average</span>}>
          <div className="flex items-center gap-3">
            <div className="text-3xl font-black text-slate-900">
              {scorePct !== null ? `${scorePct}/100` : '—'}
            </div>
          </div>
        </CompactWidget>

        <CompactWidget title="Expiring" icon="fa-triangle-exclamation" iconColor="amber" defaultExpanded={true} badge={<span className="text-xs font-bold text-amber-600">{portfolio?.expiringCount ?? '—'}</span>}>
          <div className="text-sm text-slate-600">Registrations due soon</div>
        </CompactWidget>

        <CompactWidget title="Expired" icon="fa-circle-xmark" iconColor="red" defaultExpanded={true} badge={<span className="text-xs font-bold text-red-600">{portfolio?.expiredCount ?? '—'}</span>}>
          <div className="text-sm text-slate-600">Non-compliant</div>
        </CompactWidget>

        <CompactWidget title="HVAC Readiness" icon="fa-snowflake" iconColor="sky" defaultExpanded={true} badge={hvac && hvac.propertyCount ? <span className="text-xs font-bold text-sky-600">{hvac.readyCount ?? 0}/{hvac.propertyCount} ready</span> : <span className="text-xs text-gray-400">—</span>}>
          <div className="text-sm text-slate-600">{hvac?.atRiskCount ?? 0} at risk</div>
        </CompactWidget>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <div className="hp-compact-card bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <TexasComplianceWidgets isTexasOnly />
        </div>
        <div className="hp-compact-card bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <DepositTrackerWidget />
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <div className="hp-compact-card bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <RekeyStatusWidget />
        </div>
        <div className="hp-compact-card bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <RentalRegistrationWidget />
        </div>
      </div>

      {securityScore && securityScore.pillars?.length > 0 && (
        <CompactWidget title="Security &amp; Safety Score — Pillars" icon="fa-shield-halved" iconColor="emerald" defaultExpanded={true} badge={<span className="text-[10px] font-semibold px-1.5 py-0.5 bg-emerald-50 text-emerald-700 rounded border border-emerald-200">TX Property Code § 92 Subchapter D</span>}>
          {securityScore.pillars.map((p) => {
            const pct = p.maxScore ? Math.round((p.score / p.maxScore) * 100) : 0;
            return (
              <div key={p.id} className="space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-semibold text-slate-700">{p.name}</span>
                  <span className="text-slate-500">{p.score}/{p.maxScore}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
                  <div
                    className={'h-1.5 rounded-full ' + (pct >= 70 ? 'bg-emerald-500' : pct >= 50 ? 'bg-amber-500' : 'bg-red-500')}
                    style={{ width: `${pct}%` }}
                  ></div>
                </div>
              </div>
            );
          })}
        </CompactWidget>
      )}
    </div>
  );
};

export { TexasDashboard };
export default TexasDashboard;
